/**
 * 전역 사이드바의 아코디언 (spec 2026-09-13 §3.2, 2026-09-13 Task 1b 갱신).
 *
 * 서버가 이미 활성 그룹만 data-open 으로 열어 보내므로 JS 없이도 화면은
 * 맞다. 이 모듈이 더하는 것은 사용자가 헤더를 눌러 다른 그룹을 여닫는 동작과,
 * 769-1100px 아이콘 레일에서 그룹 아이콘을 누르면 레일을 펼치면서 그 그룹을
 * 여는 동작이다.
 *
 * 여러 그룹이 동시에 열릴 수 있다(단일 열림 아님, 2026-09-13 사용자 결정) —
 * 헤더 클릭은 그 그룹만 펼치거나 접고, 형제 그룹은 건드리지 않는다. 사용자가
 * 연 그룹은 localStorage.navOpenGroups(그룹 키 배열)에 남아 페이지를 옮기거나
 * 새로고침해도 유지된다. 그룹은 "활성 그룹이다" 또는 "navOpenGroups 에 있다"
 * 둘 중 하나면 열린다 — 활성 그룹은 서버가 항상 data-open 으로 그려 보내므로
 * 그 사실만으로 열려 있고, 세션 중에 사용자가 접어도(§3.2 그대로) 다음 이동에서
 * 다시 열린다. `partials/global-sidebar.html` 의 인라인 부트스트랩 스크립트가
 * 첫 페인트 전에 같은 저장소를 읽어 깜빡임 없이 미리 펼쳐 둔다 — 그 몇 줄과
 * 아래 applyNavOpenGroups 가 같은 일을 하는 것은 의도다(K4): 부트스트랩은 모듈이
 * 뜨기 전의 첫 페인트를, 이 모듈은 사이드바 종류가 바뀌어(관리자 → 전역) 노드가
 * 통째로 갈린 뒤를 맡는다 — 갈아끼운 노드 안의 인라인 스크립트는 실행되지 않는다.
 *
 * 사이드바는 main.admin-main 밖이라 registerAdminPage 의 재초기화를 받지
 * 않고, data-sidebar 가 상수 "global" 이라 SPA 이동에도 노드가 그대로다.
 * 그래서 리스너는 요소에 딱 한 번만 붙인다 (data-bound 마커).
 */
import { setRailOpen, isRailNarrow } from './rail-tooltip.js';

const STORAGE_KEY = 'navOpenGroups';

function readOpenGroups() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr.filter((k) => typeof k === 'string') : [];
    } catch (err) {
        return [];
    }
}

function writeOpenGroups(keys) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(keys)); } catch (err) { /* 사생활 보호 모드 등: 무시 */ }
}

function setGroupOpen(group, open) {
    if (open) group.setAttribute('data-open', '1');
    else group.removeAttribute('data-open');
    const head = group.querySelector(':scope > .gnav-head');
    if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
}

/** 사용자가 명시적으로 연 그룹 키 목록 (localStorage navOpenGroups 를 읽는다). */
export function openNavGroups() {
    return readOpenGroups();
}

/** 그룹 하나의 열림 상태를 저장하고, 사이드바에 그 그룹이 있으면 즉시 반영한다. */
export function setNavGroupOpen(key, open) {
    if (!key) return;
    const keys = readOpenGroups();
    const idx = keys.indexOf(key);
    if (open && idx === -1) keys.push(key);
    else if (!open && idx !== -1) keys.splice(idx, 1);
    writeOpenGroups(keys);
    const group = document.querySelector('#adminSidebar .gnav-group[data-group="' + CSS.escape(key) + '"]');
    if (group) setGroupOpen(group, open);
}

/** 그룹 하나를 열거나 닫는다 (여러 그룹 동시 열림 — 형제는 건드리지 않는다). */
export function openNavGroup(group, open) {
    if (!group) return;
    const key = group.dataset.group;
    if (key) setNavGroupOpen(key, open);
    else setGroupOpen(group, open); // 키 없는 그룹은 없어야 하지만 보험 삼아
}

// setupGlobalSidebar() 가 노드마다 한 번 부를 때(첫 로드, 그리고 syncSidebar 가
// 관리자 → 전역으로 노드를 갈아끼운 뒤) 기억된 집합을 DOM 에 적용한다.
// 서버가 이미 그려 보낸 활성 그룹의 data-open 은 건드리지 않는다 — 그 그룹은
// 사용자가 명시적으로 닫지 않는 한 계속 열려 있어야 한다. root 를 생략하면
// 살아 있는 전역 사이드바의 <nav> 를 찾는다. (옛 swapModuleNav 호출처는
// 사라졌으므로 모듈 밖으로 내보내지 않는다.)
function applyNavOpenGroups(root) {
    const nav = root || document.querySelector('#adminSidebar [data-global-nav]');
    if (!nav) return;
    readOpenGroups().forEach((key) => {
        const group = nav.querySelector('.gnav-group[data-group="' + CSS.escape(key) + '"]');
        if (group) setGroupOpen(group, true);
    });
}

export function setupGlobalSidebar() {
    const nav = document.querySelector('#adminSidebar [data-global-nav]');
    if (!nav || nav.dataset.bound === '1') return;
    nav.dataset.bound = '1';
    applyNavOpenGroups(nav);
    nav.addEventListener('click', (event) => {
        const head = event.target.closest('.gnav-head');
        if (!head || !nav.contains(head)) return;
        event.preventDefault();
        const group = head.parentElement;
        // 레일 폭에서는 아이콘만 보인다: 먼저 레일을 펼치고 그 그룹을 연다.
        if (isRailNarrow()) {
            setRailOpen(true);
            openNavGroup(group, true);
            return;
        }
        openNavGroup(group, head.getAttribute('aria-expanded') !== 'true');
    });
    // Enter/Space 는 <button> 의 기본 동작이라 따로 다루지 않는다 (§7).
}
